import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {SandboxTree, StickerColor, StickerStateId, User} from 'yougile-model'
import {Company} from '../company'
import {globals} from '../globals-base'
import {Project} from '../project'
import {Sticker, stickerCtl} from '../sticker/sticker'
import {createDeveloperBoard} from './create-developer-board'
import {createSiteBoard} from './create-site-board'

export class FirstProject {
  public static init(onCreate: () => void): void {
    if (globals.silentMode) {
      return
    }
    if (Company.listSandboxes().length === 1) {
      Company.listSandboxes().forEach(sandbox => {
        if (FirstProject.shouldCreate(sandbox)) {
          FirstProject.create(sandbox)
          onCreate()
        }
      })
    }
  }

  private static shouldCreate(sandbox: SandboxTree): boolean {
    const user = sandbox.get(globals.currentUserId) as User
    return (
      user &&
      sandbox.getChildrenHubs(sandbox.companyId, {withDeleted: true}).length ===
        0 &&
      Math.abs(sandbox.get(sandbox.companyId).timestamp - user.timestamp) <
        60000
    )
  }

  public static async create(box: SandboxTree): Promise<void> {
    logger.log('Creating first project...')
    const project = Project.createNew(
      box.companyId,
      Lang.msg.reg.exampleProject,
      false
    )

    const lang = Lang.msg.reg

    const {getByName, create} = Sticker
    const ctl = stickerCtl.String

    /// Sticker: Исполнитель

    const userSticker = getByName(box, 'User')
    if (!userSticker) {
      return
    }

    /// Sticker: Дедлайн

    const deadlineTag = getByName(box, 'Deadline')
    if (!deadlineTag) {
      return
    }

    /// Sticker: Приоритет

    const priorSticker = create(box, lang.priorSticker, 'string', 'prio')
    if (!priorSticker) {
      return
    }
    const criPriorStickerSt = {
      id: ctl.makeShortId(),
      name: lang.criticalPrio,
      color: 'sticker-red' as StickerColor
    }
    ctl.addState(priorSticker, criPriorStickerSt)
    const majPriorStickerSt = {
      id: ctl.makeShortId(),
      name: lang.majorPrio,
      color: 'sticker-pink' as StickerColor
    }
    ctl.addState(priorSticker, majPriorStickerSt)
    const normPriorStickerSt = {
      id: ctl.makeShortId(),
      name: lang.normalPrio,
      color: 'sticker-green' as StickerColor
    }
    ctl.addState(priorSticker, normPriorStickerSt)
    const minPriorStickerSt = {
      id: ctl.makeShortId(),
      name: lang.minorPrio,
      color: 'sticker-gray' as StickerColor
    }
    ctl.addState(priorSticker, minPriorStickerSt)

    /// Sticker: Stopwatch

    const stopwatchSticker = stickerCtl.Stopwatch.getSticker(box)

    /// Sticker: Sprint

    const sprintSticker = create(box, lang.sprintSticker, 'sprint')
    if (!sprintSticker) {
      return
    }

    const sprintStickerStId = sprintSticker.states.order[0] as StickerStateId

    /// Sticker: Тип задачи

    const taskTypeSticker = create(box, lang.taskTypeSticker, 'string', 'code')
    if (!taskTypeSticker) {
      return
    }

    const featureSt = {
      id: ctl.makeShortId(),
      name: lang.taskTypeStickerStFeature,
      color: 'sticker-turquoise' as StickerColor
    }
    ctl.addState(taskTypeSticker, featureSt)
    const bugSt = {
      id: ctl.makeShortId(),
      name: lang.taskTypeStickerStBug,
      color: 'sticker-orange' as StickerColor
    }
    ctl.addState(taskTypeSticker, bugSt)

    /// Sticker: Раздел

    const sectionSticker = create(box, lang.sectionSticker, 'text')
    if (!sectionSticker) {
      return
    }

    /// Sticker: Комментарий

    const commentSticker = create(box, lang.commentSticker, 'text', 'message')
    if (!commentSticker) {
      return
    }

    /// Sticker: Устройство

    const deviceSticker = create(box, lang.deviceSticker, 'string', 'box')
    if (!deviceSticker) {
      return
    }
    const mobDeviceStickerSt = {
      id: ctl.makeShortId(),
      name: lang.mobDeviceSticker,
      color: 'sticker-violet' as StickerColor
    }
    ctl.addState(deviceSticker, mobDeviceStickerSt)
    const webDeviceStickerSt = {
      id: ctl.makeShortId(),
      name: lang.webDeviceSticker,
      color: 'sticker-blue' as StickerColor
    }
    ctl.addState(deviceSticker, webDeviceStickerSt)
    const mobWebDeviceStickerSt = {
      id: ctl.makeShortId(),
      name: lang.mobWebDeviceSticker,
      color: 'sticker-green' as StickerColor
    }
    ctl.addState(deviceSticker, mobWebDeviceStickerSt)

    ///

    /// Sticker: Оценка в днях

    const estimationSticker = create(
      box,
      lang.estimationSticker,
      'numeric',
      'history'
    )
    if (!estimationSticker) {
      return
    }

    const props = {
      userSticker,
      deadlineTag,
      priorSticker,
      criPriorStickerStId: criPriorStickerSt.id,
      majPriorStickerStId: majPriorStickerSt.id,
      normPriorStickerStId: normPriorStickerSt.id,
      minPriorStickerStId: minPriorStickerSt.id,
      stopwatchSticker,
      sprintSticker,
      sprintStickerStId: sprintStickerStId,
      taskTypeSticker,
      featureStId: featureSt.id,
      bugStId: bugSt.id,
      sectionSticker: sectionSticker,
      commentSticker: commentSticker,
      deviceSticker: deviceSticker,
      mobDeviceStickerStId: mobDeviceStickerSt.id,
      webDeviceStickerStId: webDeviceStickerSt.id,
      mobWebDeviceStickerStId: mobWebDeviceStickerSt.id,
      estimationSticker: estimationSticker
    }

    const resDevBoard = createDeveloperBoard(box, project, props)
    if (!resDevBoard) {
      return
    }

    const resTeam = createSiteBoard(box, project, props, resDevBoard)
    if (!resTeam) {
      return
    }

    const {board: teamBoard} = resTeam

    const order = Project.getBoardsOrder(project)
    order.insertBefore(teamBoard, resDevBoard.board)
  }
}
